import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import messengerApi from '../../api/messenger';

export const fetchInbox = createAsyncThunk(
  'messenger/fetchInbox',
  async ({ tab = 'people', since, view } = {}, { getState, rejectWithValue }) => {
    try {
      // Fall back to the currently-selected view in the slice so callers that
      // don't pass `view` (background pollers) honour the filter the user
      // picked in the header dropdown.
      const effectiveView = view || getState().messenger.view || 'inbox';
      const data = await messengerApi.getInbox({ tab, since, view: effectiveView });
      // Echo the view back so the reducer can tell an inbox count (which the
      // sidebar / Messenger tab badge is allowed to trust) from an archived or
      // deleted count (which it must not).
      return { ...data, view: effectiveView };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

// Sidebar + Messenger-tab badge only. Always asks for the main inbox, and
// never writes to `sessions`, so a background poll can keep the badge honest
// while the agent is looking at Archived or Deleted.
export const fetchUnreadBadge = createAsyncThunk(
  'messenger/fetchUnreadBadge',
  async (_arg, { rejectWithValue }) => {
    try {
      const data = await messengerApi.getInbox({ tab: 'people', view: 'inbox' });
      return data?.unread_total || 0;
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const bulkInboxAction = createAsyncThunk(
  'messenger/bulkInboxAction',
  async ({ action }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.bulkInboxAction(action);
      return { action, data };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const fetchThread = createAsyncThunk(
  'messenger/fetchThread',
  async (sessionId, { getState, rejectWithValue }) => {
    try {
      const thread = getState().messenger.threadsBySession[sessionId];
      const msgs = thread?.messages || [];
      let afterId;
      for (let i = msgs.length - 1; i >= 0; i -= 1) {
        const id = msgs[i]?.id;
        if (id != null && !(typeof id === 'string' && id.startsWith('pending-'))) {
          afterId = id;
          break;
        }
      }
      return await messengerApi.getThread(sessionId, { afterId });
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messenger/sendMessage',
  async ({ sessionId, content, usedSuggestion, attachment }, { rejectWithValue }) => {
    try {
      const res = await messengerApi.sendMessage(sessionId, { content, usedSuggestion, attachment });
      return { sessionId, message: res.message };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

// Right-click context-menu action on the inbox row.
// `action`: 'archive' | 'unarchive' | 'mark_unread' | 'delete'
export const sessionAction = createAsyncThunk(
  'messenger/sessionAction',
  async ({ sessionId, action }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.sessionAction(sessionId, action);
      return { sessionId, action, data };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const reactToMessage = createAsyncThunk(
  'messenger/reactToMessage',
  async ({ sessionId, messageId, emoji }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.reactToMessage(messageId, emoji);
      return { sessionId, messageId, reactions: data.reactions };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const editMessage = createAsyncThunk(
  'messenger/editMessage',
  async ({ sessionId, messageId, content }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.editMessage(messageId, content);
      return { sessionId, message: data.message };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'messenger/deleteMessage',
  async ({ sessionId, messageId }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.deleteMessage(messageId);
      return { sessionId, message: data.message };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const fetchAiContext = createAsyncThunk(
  'messenger/fetchAiContext',
  async ({ sessionId, refresh = false }, { rejectWithValue }) => {
    try {
      const data = await messengerApi.getAiContext(sessionId, { refresh });
      return { sessionId, data };
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

export const askAi = createAsyncThunk(
  'messenger/askAi',
  async ({ sessionId, question }, { getState, rejectWithValue }) => {
    // `pending` runs before the payloadCreator, so the optimistic agent bubble
    // for *this* question is already in state — slice it off so we only send
    // prior turns as history.
    const log = getState().messenger.aiQABySession[sessionId] || [];
    const history = log
      .slice(0, -1)
      .filter((t) => t.role === 'agent' || t.role === 'ai')
      .map((t) => ({ role: t.role, text: t.text }));
    try {
      const data = await messengerApi.askAi(sessionId, { question, history });
      return { sessionId, question, answer: data.answer, fallback: !!data.fallback };
    } catch (e) {
      return rejectWithValue({ sessionId, question, detail: e.response?.data || e.message });
    }
  }
);

export const escalateSession = createAsyncThunk(
  'messenger/escalate',
  async ({ sessionId, toUserId }, { rejectWithValue }) => {
    try {
      return await messengerApi.escalate(sessionId, { toUserId });
    } catch (e) {
      return rejectWithValue(e.response?.data || { detail: e.message });
    }
  }
);

const initialState = {
  tab: 'people',
  // Header dropdown filter — drives `?view=` on inbox fetches. 'inbox' is the
  // default (non-archived, non-deleted); 'archived' and 'deleted' show the
  // respective trashes.
  view: 'inbox',
  sessions: [],
  // Unread in the list currently on screen (whatever view that is).
  unreadTotal: 0,
  // Unread in the MAIN inbox, whatever the agent happens to be looking at.
  // This is the one number the sidebar badge and the Communications Hub
  // "Messenger" tab badge are drawn from, so a badge can never describe a
  // different list from the one clicking it opens.
  unreadBadge: 0,
  activeSessionId: null,
  threadsBySession: {},       // { [id]: { session, messages } }
  aiContextBySession: {},     // { [id]: { coaching_note, suggested_replies, context, risk_flags } }
  aiQABySession: {},          // { [id]: [{ role: 'agent'|'ai', text, at, fallback?, error? }] }
  loadingInbox: false,
  loadingThread: false,
  loadingAi: false,
  loadingAiAsk: false,
  sendingMessage: false,
  error: null,
};

const messengerSlice = createSlice({
  name: 'messenger',
  initialState,
  reducers: {
    setActiveSession(state, action) {
      state.activeSessionId = action.payload;
    },
    setTab(state, action) {
      state.tab = action.payload;
    },
    setView(state, action) {
      state.view = action.payload || 'inbox';
      // Clear the visible list so we don't briefly show the previous view's
      // rows while the new fetch is in flight.
      state.sessions = [];
      state.unreadTotal = 0;
      // `unreadBadge` is deliberately left alone: switching to Archived does
      // not empty the inbox.
    },
    clearError(state) { state.error = null; },
    // optimistic insert when the agent submits a reply
    insertOptimisticMessage(state, action) {
      const { sessionId, message } = action.payload;
      let thread = state.threadsBySession[sessionId];
      if (!thread) {
        const summary = state.sessions.find((s) => s.id === sessionId);
        thread = { session: summary || { id: sessionId }, messages: [], typing: null };
        state.threadsBySession[sessionId] = thread;
      }
      thread.messages.push(message);
    },
    // Insert a message pushed via WebSocket. Deduped by id so a broadcast that
    // arrives before (or after) sendMessage.fulfilled doesn't duplicate, and
    // pending optimistic placeholders for the same sender get replaced rather
    // than left behind as ghosts when the broadcast wins the race.
    receiveBroadcastMessage(state, action) {
      const { sessionId, message } = action.payload || {};
      if (!sessionId || !message || message.id == null) return;
      let thread = state.threadsBySession[sessionId];
      if (!thread) {
        const summary = state.sessions.find((s) => s.id === sessionId);
        thread = { session: summary || { id: sessionId }, messages: [], typing: null };
        state.threadsBySession[sessionId] = thread;
      }
      if (thread.messages.some((m) => m.id === message.id)) return;
      const pendingIdx = thread.messages.findIndex(
        (m) =>
          typeof m.id === 'string' &&
          m.id.startsWith('pending-') &&
          m.sender_id === message.sender_id,
      );
      if (pendingIdx >= 0) {
        thread.messages[pendingIdx] = message;
      } else {
        thread.messages.push(message);
      }
    },
    setThreadTyping(state, action) {
      const { sessionId, typing } = action.payload || {};
      if (!sessionId) return;
      const thread = state.threadsBySession[sessionId];
      if (thread) thread.typing = typing || null;
    },
    // Mark every own message in the thread with id <= upToMessageId as read.
    // Used both for the polling `read_through` field and the WebSocket `read`
    // event so the double-tick flips the moment the peer sees the message.
    applyReadReceipt(state, action) {
      const { sessionId, upToMessageId, readAt } = action.payload || {};
      if (!sessionId || !upToMessageId) return;
      const thread = state.threadsBySession[sessionId];
      if (!thread?.messages) return;
      const stamp = readAt || new Date().toISOString();
      thread.messages.forEach((m) => {
        if (m.read_at) return;
        if (m.role !== 'agent') return; // only flip own (agent-side) bubbles
        if (typeof m.id !== 'number' || m.id > upToMessageId) return;
        m.read_at = stamp;
      });
    },
    clearAiQA(state, action) {
      const sessionId = action.payload;
      if (sessionId in state.aiQABySession) {
        state.aiQABySession[sessionId] = [];
      }
    },
    // Replace an existing bubble in place after it was edited or soft-deleted —
    // used both for the WS `message.updated` push and the local edit/delete
    // thunk responses. Deleted messages arrive with `is_deleted` set and
    // blanked content so the bubble renders a tombstone.
    applyMessageUpdate(state, action) {
      const { sessionId, message } = action.payload || {};
      if (!sessionId || !message || message.id == null) return;
      const thread = state.threadsBySession[sessionId];
      if (!thread?.messages) return;
      const idx = thread.messages.findIndex((m) => m.id === message.id);
      if (idx >= 0) thread.messages[idx] = { ...thread.messages[idx], ...message };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInbox.pending, (state) => { state.loadingInbox = true; })
      .addCase(fetchInbox.fulfilled, (state, { payload }) => {
        state.loadingInbox = false;
        state.sessions = payload.sessions || [];
        state.unreadTotal = payload.unread_total || 0;
        if ((payload.view || 'inbox') === 'inbox') state.unreadBadge = state.unreadTotal;
      })
      .addCase(fetchInbox.rejected, (state, { payload }) => {
        state.loadingInbox = false;
        state.error = payload;
      })
      .addCase(fetchUnreadBadge.fulfilled, (state, { payload }) => {
        state.unreadBadge = payload || 0;
      })
      .addCase(fetchThread.pending, (state, action) => {
        const sessionId = action.meta.arg;
        // Only flag loading when this session hasn't been loaded yet (avoids
        // spinner flicker on background incremental polls).
        state.loadingThread = !state.threadsBySession[sessionId];
      })
      .addCase(fetchThread.fulfilled, (state, { payload }) => {
        state.loadingThread = false;
        const sid = payload?.session?.id;
        if (!sid) return;

        // Flip the double-tick on already-known own bubbles whose read_at
        // changed since the previous poll. Without this the read receipt
        // never updates because incremental polls only return new messages.
        const through = payload.read_through;
        if (through?.read_through_id) {
          const existing = state.threadsBySession[sid];
          if (existing?.messages) {
            const stamp = through.read_through_at || new Date().toISOString();
            existing.messages.forEach((m) => {
              if (m.read_at) return;
              if (m.role !== 'agent') return;
              if (typeof m.id !== 'number' || m.id > through.read_through_id) return;
              m.read_at = stamp;
            });
          }
        }

        // The thread GET endpoint marks unread messages as read on the server,
        // so optimistically zero this session's unread count and recompute the
        // sidebar total. Without this the badge persists until the next inbox
        // poll (up to 30s on WS, 5s on fallback).
        const sessionIdx = state.sessions.findIndex((s) => s.id === sid);
        if (sessionIdx >= 0) {
          const prevUnread = state.sessions[sessionIdx].unread_count || 0;
          if (prevUnread > 0) {
            state.sessions[sessionIdx] = { ...state.sessions[sessionIdx], unread_count: 0 };
            state.unreadTotal = Math.max(0, (state.unreadTotal || 0) - prevUnread);
            state.unreadBadge = Math.max(0, (state.unreadBadge || 0) - prevUnread);
          }
        }

        const existing = state.threadsBySession[sid];
        const incoming = payload.messages || [];

        if (payload.incremental && existing?.messages) {
          const pending = existing.messages.filter(
            (m) => typeof m.id === 'string' && m.id.startsWith('pending-')
          );
          const base = existing.messages.filter(
            (m) => !(typeof m.id === 'string' && m.id.startsWith('pending-'))
          );
          incoming.forEach((m) => {
            if (!base.some((x) => x.id === m.id)) base.push(m);
          });
          existing.messages = [...base, ...pending];
          existing.session = payload.session;
          existing.typing = payload.typing;
        } else {
          // `typing` is included in the thread response — peer's typing state
          // (the buyer, when this is the agent fetching). Keep it on the same
          // shape so MessengerThread can render the indicator.
          const pending = existing?.messages?.filter(
            (m) => typeof m.id === 'string' && m.id.startsWith('pending-')
          ) || [];
          state.threadsBySession[sid] = pending.length
            ? { ...payload, messages: [...(payload.messages || []), ...pending] }
            : payload;
        }
      })
      .addCase(fetchThread.rejected, (state, { payload }) => {
        state.loadingThread = false;
        state.error = payload;
      })
      .addCase(sendMessage.pending, (state) => { state.sendingMessage = true; })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        state.sendingMessage = false;
        const t = state.threadsBySession[payload.sessionId];
        if (!t) return;
        // If a WS broadcast already inserted this real message, just drop the
        // pending placeholder — replacing it would create a duplicate.
        const alreadyInserted = t.messages.some((m) => m.id === payload.message.id);
        if (alreadyInserted) {
          t.messages = t.messages.filter(
            (m) => !(typeof m.id === 'string' && m.id.startsWith('pending-')),
          );
          return;
        }
        const pendingIdx = t.messages.findIndex(
          (m) => typeof m.id === 'string' && m.id.startsWith('pending-'),
        );
        if (pendingIdx >= 0) {
          t.messages[pendingIdx] = payload.message;
        } else {
          t.messages.push(payload.message);
        }
      })
      .addCase(sendMessage.rejected, (state, { meta, payload }) => {
        state.sendingMessage = false;
        state.error = payload;
        const sessionId = meta?.arg?.sessionId;
        const t = sessionId ? state.threadsBySession[sessionId] : null;
        if (t) {
          t.messages = t.messages.filter(
            (m) => !(typeof m.id === 'string' && m.id.startsWith('pending-'))
          );
        }
      })
      .addCase(fetchAiContext.pending, (state) => { state.loadingAi = true; })
      .addCase(fetchAiContext.fulfilled, (state, { payload }) => {
        state.loadingAi = false;
        state.aiContextBySession[payload.sessionId] = payload.data;
      })
      .addCase(fetchAiContext.rejected, (state, { payload }) => {
        state.loadingAi = false;
        state.error = payload;
      })
      .addCase(askAi.pending, (state, { meta }) => {
        const { sessionId, question } = meta.arg;
        state.loadingAiAsk = true;
        const log = state.aiQABySession[sessionId] || [];
        log.push({ role: 'agent', text: question, at: new Date().toISOString() });
        state.aiQABySession[sessionId] = log;
      })
      .addCase(askAi.fulfilled, (state, { payload }) => {
        state.loadingAiAsk = false;
        const log = state.aiQABySession[payload.sessionId] || [];
        log.push({
          role: 'ai',
          text: payload.answer,
          at: new Date().toISOString(),
          fallback: payload.fallback,
        });
        state.aiQABySession[payload.sessionId] = log;
      })
      .addCase(sessionAction.fulfilled, (state, { payload }) => {
        const { sessionId, action } = payload;
        // archive / delete / restore all transition the row out of the
        // currently-visible view. We drop it locally and let the next inbox
        // refetch bring it back if the view changes.
        if (['archive', 'delete', 'unarchive', 'restore'].includes(action)) {
          const idx = state.sessions.findIndex((s) => s.id === sessionId);
          if (idx >= 0) {
            const removed = state.sessions[idx];
            const gone = removed.unread_count || 0;
            state.unreadTotal = Math.max(0, (state.unreadTotal || 0) - gone);
            // The badge only ever counts the main inbox: archiving or deleting
            // from it takes those unread out, un-archiving or restoring from
            // one of the trashes puts them back in.
            state.unreadBadge = state.view === 'inbox'
              ? Math.max(0, (state.unreadBadge || 0) - gone)
              : (state.unreadBadge || 0) + gone;
            state.sessions.splice(idx, 1);
          }
          if (state.activeSessionId === sessionId) state.activeSessionId = null;
        } else if (action === 'mark_unread') {
          const idx = state.sessions.findIndex((s) => s.id === sessionId);
          if (idx >= 0 && (state.sessions[idx].unread_count || 0) === 0) {
            state.sessions[idx] = { ...state.sessions[idx], unread_count: 1 };
            state.unreadTotal = (state.unreadTotal || 0) + 1;
            if (state.view === 'inbox') state.unreadBadge = (state.unreadBadge || 0) + 1;
          }
        }
      })
      .addCase(bulkInboxAction.fulfilled, (state, { payload }) => {
        if (payload.action === 'mark_all_read') {
          state.sessions = state.sessions.map((s) => ({ ...s, unread_count: 0 }));
          state.unreadTotal = 0;
          state.unreadBadge = 0;
        }
      })
      .addCase(reactToMessage.fulfilled, (state, { payload }) => {
        const { sessionId, messageId, reactions } = payload;
        const thread = state.threadsBySession[sessionId];
        if (!thread?.messages) return;
        const m = thread.messages.find((x) => x.id === messageId);
        if (m) m.reactions = reactions || {};
      })
      .addCase(editMessage.fulfilled, (state, { payload }) => {
        const { sessionId, message } = payload;
        const thread = state.threadsBySession[sessionId];
        if (!thread?.messages || !message) return;
        const idx = thread.messages.findIndex((m) => m.id === message.id);
        if (idx >= 0) thread.messages[idx] = { ...thread.messages[idx], ...message };
      })
      .addCase(deleteMessage.fulfilled, (state, { payload }) => {
        const { sessionId, message } = payload;
        const thread = state.threadsBySession[sessionId];
        if (!thread?.messages || !message) return;
        const idx = thread.messages.findIndex((m) => m.id === message.id);
        if (idx >= 0) thread.messages[idx] = { ...thread.messages[idx], ...message };
      })
      .addCase(askAi.rejected, (state, { payload }) => {
        state.loadingAiAsk = false;
        if (payload?.sessionId) {
          const log = state.aiQABySession[payload.sessionId] || [];
          log.push({
            role: 'ai',
            text: '⚠️ AI failed to answer — try again.',
            at: new Date().toISOString(),
            error: true,
          });
          state.aiQABySession[payload.sessionId] = log;
        }
      });
  },
});

export const {
  setActiveSession,
  setTab,
  setView,
  clearError,
  insertOptimisticMessage,
  receiveBroadcastMessage,
  setThreadTyping,
  applyReadReceipt,
  applyMessageUpdate,
  clearAiQA,
} = messengerSlice.actions;
export default messengerSlice.reducer;
